import React, { useState, useEffect } from 'react';
import { authenticatedFetch, getAuthToken, getBackendUrl } from '../utils/auth';
import './CameraList.css';

const API_BASE_URL = getBackendUrl();

const CameraList = ({ onCameraSelect, onAuthError }) => {
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [screenshotTimestamps, setScreenshotTimestamps] = useState({});
  const [imageAspectRatios, setImageAspectRatios] = useState({});

  // Fetch cameras from API
  const fetchCameras = async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/cameras`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setCameras(data.cameras || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching cameras:', err);
      if (err.message === 'Authentication failed' || err.message === 'Not authenticated') {
        // Handle authentication error
        if (onAuthError) {
          onAuthError();
        }
        return;
      }
      setError(`Failed to fetch cameras: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Update screenshot timestamp to force refresh
  const updateScreenshotTimestamp = (cameraId) => {
    setScreenshotTimestamps(prev => ({
      ...prev,
      [cameraId]: Date.now()
    }));
  };

  // Handle image load to detect aspect ratio
  const handleImageLoad = (cameraId, event) => {
    const img = event.target;
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    setImageAspectRatios(prev => ({
      ...prev,
      [cameraId]: aspectRatio
    }));
  };

  // Handle camera selection
  const handleCameraClick = (camera) => {
    if (onCameraSelect) {
      onCameraSelect(camera);
    }
  };

  // Initialize cameras on mount
  useEffect(() => {
    fetchCameras();
  }, []);

  // Set up screenshot refresh intervals
  useEffect(() => {
    if (cameras.length === 0) return;

    // Initialize timestamps for all cameras
    const initialTimestamps = {};
    cameras.forEach(camera => {
      initialTimestamps[camera.camera_id] = Date.now();
    });
    setScreenshotTimestamps(initialTimestamps);

    // Set up 12-second intervals for each camera
    const intervals = cameras.map(camera => {
      return setInterval(() => {
        updateScreenshotTimestamp(camera.camera_id);
      }, 12000);
    });

    return () => {
      intervals.forEach(interval => clearInterval(interval));
    };
  }, [cameras]);

  if (loading) {
    return (
      <div className="camera-list">
        <div className="loading">Loading cameras...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="camera-list">
        <div className="error">Error: {error}</div>
        <div className="error-details">
          Make sure your backend API is running on {API_BASE_URL.replace('/api', '')}
        </div>
        <button onClick={fetchCameras} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (cameras.length === 0) {
    return (
      <div className="camera-list">
        <div className="no-cameras">No cameras found</div>
      </div>
    );
  }

  return (
    <div className="camera-list">
      <h1>Camera Dashboard</h1>
      <div className="cameras-grid">
        {cameras.map(camera => {
          const aspectRatio = imageAspectRatios[camera.camera_id] || 16/9; // Default to 16:9
          return (
            <div 
              key={camera.camera_id} 
              className="camera-card"
              onClick={() => handleCameraClick(camera)}
            >
              <div 
                className="camera-screenshot"
                style={{ aspectRatio: aspectRatio }}
              >
                <img
                  src={`${API_BASE_URL}/camera_screenshot/${camera.camera_id}?t=${screenshotTimestamps[camera.camera_id] || Date.now()}&token=${getAuthToken()}`}
                  alt={`${camera.name} screenshot`}
                  onLoad={(e) => handleImageLoad(camera.camera_id, e)}
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjI0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                  }}
                />
                <div className="camera-overlay">
                  <h3 className="camera-name">{camera.name}</h3>
                  <div className="play-button">
                    <div className="play-icon"></div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CameraList; 